import { useState } from "react";
import { styles } from "../styles";
import BackButton from "../components/BackButton";

// All Settings cards collapse by default so the page reads as a small
// menu and the admin only opens what they're editing. Title bar is
// always visible; arrow flips when open. Each card carries its own
// state so opening one doesn't reset another in mid-edit.
function CollapsibleCard({ title, subtitle, maxWidth, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ ...styles.card, maxWidth: maxWidth || undefined }}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          width: "100%",
          padding: 0,
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          fontFamily: "inherit",
        }}
      >
        <span style={{ fontWeight: 700, fontSize: "15px" }}>{title}</span>
        <span style={{ color: "#5a6470", fontSize: "12px" }}>
          {open ? "▾" : "▸"}
        </span>
      </button>
      {!open && subtitle && (
        <div
          style={{
            fontSize: "12px",
            color: "#666",
            marginTop: "4px",
          }}
        >
          {subtitle}
        </div>
      )}
      {open && (
        <div style={{ marginTop: "10px" }}>{children}</div>
      )}
    </div>
  );
}

export default function CompanySettingsPage({
  orgName,
  logoUrl,
  orgAddress = {},
  payPeriodConfig,
  quoteDefaults,
  maintenanceDefaults,
  actions,
  onBack,
}) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const [street, setStreet] = useState(orgAddress.street || "");
  const [city, setCity] = useState(orgAddress.city || "");
  const [state, setState] = useState(orgAddress.state || "");
  const [zip, setZip] = useState(orgAddress.zip || "");
  const [phone, setPhone] = useState(orgAddress.phone || "");
  const [savingAddr, setSavingAddr] = useState(false);
  const [addrMsg, setAddrMsg] = useState("");

  // Pay period config (Piece 2). Local draft so the user can pick
  // frequency / start day / anchor before saving.
  const [ppFrequency, setPpFrequency] = useState(
    payPeriodConfig?.frequency || "weekly"
  );
  const [ppStartDay, setPpStartDay] = useState(
    payPeriodConfig?.startDayOfWeek || "monday"
  );
  const [ppAnchor, setPpAnchor] = useState(payPeriodConfig?.anchorDate || "");
  const [savingPp, setSavingPp] = useState(false);
  const [ppMsg, setPpMsg] = useState("");

  // Quote & Bill defaults (Piece: org billing defaults). Local draft +
  // save. Pre-fills quote.terms / validDays / footer on new quotes, and
  // labor cost/charge + material/charge markup on new line items.
  const [qdTerms, setQdTerms] = useState(quoteDefaults?.terms || "");
  const [qdValidDays, setQdValidDays] = useState(
    quoteDefaults?.validDays ?? 30
  );
  const [qdFooter, setQdFooter] = useState(quoteDefaults?.footer || "");
  const [qdLaborCost, setQdLaborCost] = useState(
    quoteDefaults?.laborCost ?? ""
  );
  const [qdLaborCharge, setQdLaborCharge] = useState(
    quoteDefaults?.laborCharge ?? ""
  );
  const [qdMaterialMarkup, setQdMaterialMarkup] = useState(
    quoteDefaults?.materialMarkup ?? ""
  );
  const [qdChargeMarkup, setQdChargeMarkup] = useState(
    quoteDefaults?.chargeMarkup ?? ""
  );
  const [savingQd, setSavingQd] = useState(false);
  const [qdMsg, setQdMsg] = useState("");

  const saveQuoteDefaults = async () => {
    setQdMsg("");
    try {
      setSavingQd(true);
      await actions.setOrgQuoteDefaults({
        terms: qdTerms,
        validDays: qdValidDays,
        footer: qdFooter,
        laborCost: qdLaborCost,
        laborCharge: qdLaborCharge,
        materialMarkup: qdMaterialMarkup,
        chargeMarkup: qdChargeMarkup,
      });
      setQdMsg("Quote defaults saved.");
    } catch (e) {
      setQdMsg(`Couldn't save: ${e.message}`);
    } finally {
      setSavingQd(false);
    }
  };

  // Org-wide maintenance bill automation default. New contracts inherit
  // this unless the admin sets an explicit per-contract override. Drives
  // whether closing a per-visit contract's visit auto-fills a charge
  // line for the contracted price.
  const [maintBillAutomation, setMaintBillAutomation] = useState(
    maintenanceDefaults?.billAutomation || "off"
  );
  const [savingMaint, setSavingMaint] = useState(false);
  const [maintMsg, setMaintMsg] = useState("");

  const saveMaintenanceDefaults = async () => {
    setMaintMsg("");
    try {
      setSavingMaint(true);
      await actions.setOrgMaintenanceDefaults({
        billAutomation: maintBillAutomation,
      });
      setMaintMsg("Maintenance defaults saved.");
    } catch (e) {
      setMaintMsg(`Couldn't save: ${e.message}`);
    } finally {
      setSavingMaint(false);
    }
  };

  const savePayPeriod = async () => {
    setPpMsg("");
    if (ppFrequency === "biweekly" && !ppAnchor) {
      setPpMsg("Biweekly needs an anchor date (any past pay-period start).");
      return;
    }
    try {
      setSavingPp(true);
      await actions.setOrgPayPeriodConfig({
        frequency: ppFrequency,
        startDayOfWeek: ppStartDay,
        anchorDate: ppFrequency === "biweekly" ? ppAnchor : "",
      });
      setPpMsg("Pay period saved.");
    } catch (e) {
      setPpMsg(`Couldn't save: ${e.message}`);
    } finally {
      setSavingPp(false);
    }
  };

  const saveAddress = async () => {
    try {
      setSavingAddr(true);
      setAddrMsg("");
      await actions.setOrgAddress({ street, city, state, zip, phone });
      setAddrMsg("Address saved.");
    } catch (e) {
      setAddrMsg(`Couldn't save: ${e.message}`);
    } finally {
      setSavingAddr(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setErr("Logo must be under 2 MB.");
      return;
    }
    try {
      setBusy(true);
      setErr("");
      setMsg("");
      await actions.uploadOrgLogo(file);
      setMsg("Logo updated.");
      setFile(null);
      const input = document.getElementById("org-logo-input");
      if (input) input.value = "";
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("Remove the company logo?")) return;
    try {
      setBusy(true);
      setErr("");
      setMsg("");
      await actions.removeOrgLogo();
      setMsg("Logo removed.");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>Company Settings</h2>
      <p style={{ fontSize: "12px", color: "#666", margin: "0 0 12px" }}>
        Org: <strong>{orgName || "—"}</strong>
      </p>

      {err && (
        <div style={{ color: "red", marginBottom: "8px", fontSize: "13px" }}>
          {err}
        </div>
      )}
      {msg && (
        <div style={{ color: "#1a7f37", marginBottom: "8px", fontSize: "13px" }}>
          {msg}
        </div>
      )}

      <CollapsibleCard
        title="Company Logo"
        subtitle="Shown in your app header and every PDF."
        maxWidth="780px"
      >
        <p style={{ fontSize: "12px", color: "#666", margin: "4px 0 10px" }}>
          Shown in your app header and on every PDF you print (signed
          tickets, invoices, quotes). PNG, JPG, or SVG. Recommended: 200–400
          pixels wide on a transparent background. Max 2 MB.
        </p>

        {logoUrl ? (
          <div
            style={{
              border: "1px solid #ddd",
              borderRadius: "6px",
              padding: "12px",
              background: "white",
              display: "inline-block",
              marginBottom: "12px",
            }}
          >
            <img
              src={logoUrl}
              alt="Current company logo"
              style={{
                maxHeight: "100px",
                maxWidth: "300px",
                display: "block",
              }}
            />
          </div>
        ) : (
          <p style={{ fontSize: "12px", color: "#888", fontStyle: "italic" }}>
            No logo uploaded yet.
          </p>
        )}

        <div
          style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}
        >
          <label
            htmlFor="org-logo-input"
            style={{
              ...styles.button,
              margin: 0,
              display: "inline-block",
              cursor: "pointer",
            }}
          >
            Choose file
          </label>
          <input
            id="org-logo-input"
            type="file"
            accept="image/png,image/jpeg,image/svg+xml"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            style={{ display: "none" }}
          />
          {file && (
            <span style={{ fontSize: "12px", color: "#666" }}>
              {file.name} ({Math.round(file.size / 1024)} KB)
            </span>
          )}
          <button
            style={{ ...styles.primaryButton, margin: 0 }}
            onClick={handleUpload}
            disabled={!file || busy}
          >
            {busy ? "Uploading..." : logoUrl ? "Replace Logo" : "Upload Logo"}
          </button>
          {logoUrl && (
            <button
              style={{
                ...styles.button,
                margin: 0,
                background: "#fee",
                color: "#a00",
              }}
              onClick={handleRemove}
              disabled={busy}
            >
              Remove Logo
            </button>
          )}
        </div>
      </CollapsibleCard>

      <CollapsibleCard
        title="Company Address"
        subtitle="Printed on every customer-facing document."
        maxWidth="780px"
      >
        <p style={{ fontSize: "12px", color: "#666", margin: "4px 0 10px" }}>
          Shown on every bill, quote, invoice, and signed ticket your
          customers receive. Lets them know who sent it and where to send
          payment.
        </p>
        <input
          style={styles.input}
          placeholder="Street address"
          value={street}
          onChange={(e) => setStreet(e.target.value)}
        />
        <input
          style={styles.input}
          placeholder="City"
          value={city}
          onChange={(e) => setCity(e.target.value)}
        />
        <input
          style={styles.input}
          placeholder="State"
          value={state}
          onChange={(e) => setState(e.target.value)}
        />
        <input
          style={styles.input}
          placeholder="ZIP"
          value={zip}
          onChange={(e) => setZip(e.target.value)}
        />
        <input
          style={styles.input}
          type="tel"
          placeholder="Phone (optional)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <button
          style={{ ...styles.primaryButton, margin: "8px 0 0" }}
          onClick={saveAddress}
          disabled={savingAddr}
        >
          {savingAddr ? "Saving..." : "Save Address"}
        </button>
        {addrMsg && (
          <span style={{ fontSize: "12px", marginLeft: "8px" }}>{addrMsg}</span>
        )}
      </CollapsibleCard>

      {/* Quote & Bill Defaults. Seeds new quotes + new line items in
          QuoteEditor / JobCosting. Each individual quote can still
          override per-deal. */}
      <CollapsibleCard
        title="Quote & Bill Defaults"
        subtitle="Pre-fill terms, validity, line item rates + markups."
      >
        <p style={{ fontSize: "12px", color: "#666", margin: "0 0 12px" }}>
          Auto-fills new quotes + new line items so admin / sales don't
          retype the same terms and rates every time. Each quote can
          still override per deal.
        </p>

        <div style={{ marginBottom: "10px" }}>
          <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
            Default Terms (newline-separated; appear on quote PDF)
          </label>
          <textarea
            style={{ ...styles.textarea, margin: 0, width: "100%", minHeight: "80px" }}
            value={qdTerms}
            onChange={(e) => setQdTerms(e.target.value)}
            placeholder="Net 30. 50% deposit required for jobs over $5,000. Warranty: 1 year parts and labor on installation."
          />
        </div>

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "10px" }}>
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
              Validity (days)
            </label>
            <input
              type="number"
              min="1"
              style={{ ...styles.input, margin: 0, width: "120px" }}
              value={qdValidDays}
              onChange={(e) => setQdValidDays(e.target.value)}
            />
          </div>
          <div style={{ flex: 1, minWidth: "260px" }}>
            <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
              Quote Footer (centered at bottom of customer PDF)
            </label>
            <textarea
              style={{ ...styles.textarea, margin: 0, width: "100%", minHeight: "50px" }}
              value={qdFooter}
              onChange={(e) => setQdFooter(e.target.value)}
              placeholder="TX License #TACL12345. Thank you for your business."
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "10px" }}>
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
              Default Labor Cost ($/hr)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              style={{ ...styles.input, margin: 0, width: "140px" }}
              value={qdLaborCost}
              onChange={(e) => setQdLaborCost(e.target.value)}
              placeholder="e.g. 45"
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
              Default Labor Charge ($/hr)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              style={{ ...styles.input, margin: 0, width: "140px" }}
              value={qdLaborCharge}
              onChange={(e) => setQdLaborCharge(e.target.value)}
              placeholder="e.g. 125"
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
              Default Material Markup (%)
            </label>
            <input
              type="number"
              min="0"
              step="0.1"
              style={{ ...styles.input, margin: 0, width: "140px" }}
              value={qdMaterialMarkup}
              onChange={(e) => setQdMaterialMarkup(e.target.value)}
              placeholder="e.g. 35"
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
              Default Charge Markup (%)
            </label>
            <input
              type="number"
              min="0"
              step="0.1"
              style={{ ...styles.input, margin: 0, width: "140px" }}
              value={qdChargeMarkup}
              onChange={(e) => setQdChargeMarkup(e.target.value)}
              placeholder="e.g. 25"
            />
          </div>
        </div>

        <button
          style={{ ...styles.primaryButton, margin: "8px 0 0" }}
          onClick={saveQuoteDefaults}
          disabled={savingQd}
        >
          {savingQd ? "Saving..." : "Save Defaults"}
        </button>
        {qdMsg && (
          <span style={{ fontSize: "12px", marginLeft: "8px" }}>{qdMsg}</span>
        )}
      </CollapsibleCard>

      {/* Maintenance bill automation default. Per-contract override
          available on ContractDetailPage. Phase 1 only covers per-visit
          contracts (auto-draft fires on visit completion); per-interval
          (calendar-driven) lands in Phase 1.5. */}
      <CollapsibleCard
        title="Maintenance Defaults"
        subtitle="Auto-draft the per-visit bill when a visit's marked complete."
      >
        <p style={{ fontSize: "12px", color: "#666", margin: "0 0 12px" }}>
          Controls what happens when a per-visit contract's visit gets
          marked complete. Each contract can still override this default.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <label
            style={{
              display: "flex",
              gap: "8px",
              alignItems: "flex-start",
              cursor: "pointer",
            }}
          >
            <input
              type="radio"
              name="maintBillAutomation"
              value="off"
              checked={maintBillAutomation === "off"}
              onChange={() => setMaintBillAutomation("off")}
              style={{ marginTop: "3px" }}
            />
            <span>
              <strong>Off (manual)</strong>
              <div style={{ fontSize: "12px", color: "#666" }}>
                Office builds every maintenance bill from scratch. Today's
                behavior — no surprises.
              </div>
            </span>
          </label>
          <label
            style={{
              display: "flex",
              gap: "8px",
              alignItems: "flex-start",
              cursor: "pointer",
            }}
          >
            <input
              type="radio"
              name="maintBillAutomation"
              value="auto-draft"
              checked={maintBillAutomation === "auto-draft"}
              onChange={() => setMaintBillAutomation("auto-draft")}
              style={{ marginTop: "3px" }}
            />
            <span>
              <strong>Auto-draft</strong>
              <div style={{ fontSize: "12px", color: "#666" }}>
                When a visit's marked complete, the contracted per-visit
                price is auto-added as a charge line. Office still reviews
                and finalizes — nothing auto-sends.
              </div>
            </span>
          </label>
        </div>
        <button
          style={{ ...styles.primaryButton, margin: "12px 0 0" }}
          onClick={saveMaintenanceDefaults}
          disabled={savingMaint}
        >
          {savingMaint ? "Saving..." : "Save Maintenance Defaults"}
        </button>
        {maintMsg && (
          <span style={{ fontSize: "12px", marginLeft: "8px" }}>{maintMsg}</span>
        )}
      </CollapsibleCard>

      {/* Pay Period block (Piece 2). Drives the "Current pay period"
          filter on PayrollPage + the start-of-week boundary for
          group-by-week. */}
      <CollapsibleCard
        title="Pay Period"
        subtitle="Weekly / biweekly / monthly + start-of-week day."
      >
        <p style={{ fontSize: "12px", color: "#666", margin: "0 0 12px" }}>
          Sets the active pay-period window for one-click payroll filters
          + CSV exports. Also pins the start-of-week for "Group by Week"
          in Payroll Overview.
        </p>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
              Frequency
            </label>
            <select
              style={{ ...styles.input, margin: 0, width: "160px" }}
              value={ppFrequency}
              onChange={(e) => setPpFrequency(e.target.value)}
            >
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
              Week starts on
            </label>
            <select
              style={{ ...styles.input, margin: 0, width: "140px" }}
              value={ppStartDay}
              onChange={(e) => setPpStartDay(e.target.value)}
              disabled={ppFrequency === "monthly"}
            >
              <option value="sunday">Sunday</option>
              <option value="monday">Monday</option>
              <option value="tuesday">Tuesday</option>
              <option value="wednesday">Wednesday</option>
              <option value="thursday">Thursday</option>
              <option value="friday">Friday</option>
              <option value="saturday">Saturday</option>
            </select>
          </div>
          {ppFrequency === "biweekly" && (
            <div>
              <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
                Anchor date
              </label>
              <input
                type="date"
                style={{ ...styles.input, margin: 0, width: "160px" }}
                value={ppAnchor}
                onChange={(e) => setPpAnchor(e.target.value)}
                title="Any past pay-period start date. The cycle ripples forward from here every 14 days."
              />
            </div>
          )}
        </div>
        <button
          style={{ ...styles.primaryButton, margin: "12px 0 0" }}
          onClick={savePayPeriod}
          disabled={savingPp}
        >
          {savingPp ? "Saving..." : "Save Pay Period"}
        </button>
        {ppMsg && (
          <span style={{ fontSize: "12px", marginLeft: "8px" }}>{ppMsg}</span>
        )}
      </CollapsibleCard>
    </>
  );
}
